import { Link } from "react-router-dom";
import Page from "@/components/layout/Page";
import autismHandprint from "@/assets/mental-health/autism-handprint.png";
import nhsBadge from "@/assets/nhs-mental-health-badge.png";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";

export default function Autism() {
  return (
    <>
      <SEOHead
        title="Autism Spectrum Disorder (ASD) Support | Sensory Tools | Samboards"
        description="Handmade tactile sensory tools for autism spectrum support. Help with self-regulation, focus & sensory needs. NHS-recommended. Perfect for SEN schools & therapy. UK made."
        canonical="https://samboards.co.uk/mental-health/autism"
        keywords="autism sensory tools, ASD support tools, autism tactile boards, sensory regulation autism, SEN autism resources, autism therapy tools, NHS autism support, UK handmade"
        ogImage={autismHandprint}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Mental Health", url: "/mental-health/autism" },
          { name: "Autism", url: "/mental-health/autism" }
        ]}
      />
      
      <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <Page>
        <section className="w-full pb-16 sm:pb-20 md:pb-24">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4 sm:gap-6 mb-4 sm:mb-6">
            <h1 className="text-[36px] sm:text-[46px] lg:text-[54px] leading-[1.1] font-bold page-title-gradient flex-1">Autism Spectrum Disorder (ASD)</h1>
            <a 
              href="https://www.nhs.uk/mental-health/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex-shrink-0 transition-all duration-200 hover:scale-105 hover:opacity-90 touch-manipulation"
              aria-label="NHS Mental Health Support"
            >
              <img 
                src={nhsBadge} 
                alt="NHS - Here to help with mental health" 
                className="h-12 sm:h-16 md:h-20 w-auto object-contain"
              />
            </a>
          </div>
          
          <div className="mental-health-hero-image">
            <img 
              src={autismHandprint} 
              alt="Autism awareness illustrated with colorful handprints"
            />
          </div>
          
          <div className="space-y-5 sm:space-y-6 max-w-3xl">
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
              Autism (ASD), refers to a broad range of conditions characterised by challenges with social skills, behaviour, speech and nonverbal communication.
            </p>
            
            <p className="text-base sm:text-lg leading-relaxed">
              A recent study shows that 700,000 individuals in the UK are classified as being on the autism spectrum. One of the common issues experienced is sensory processing disorder and is now considered to be a symptom of autism. It is a condition in which the brain has difficulty in receiving and responding to information it has to process.
            </p>
            
            <p className="text-base sm:text-lg leading-relaxed">
              Samboards products are particularly helpful to individuals who suffer from any condition affecting learning and concentration, especially with children who suffer from Autism Spectrum Disorder (ASD) as they struggle to control their anger, frustration and inability to focus.
            </p>
            
            <p className="text-base sm:text-lg leading-relaxed">
              Of course, they should be used in conjunction with the programmes recommended by the Occupational Therapist, and not as a means of replacement.
            </p>
            
            <p className="text-base sm:text-lg leading-relaxed">
              The Samboard Sensory Pads are designed to provide the child with a calming and fun activity, at the same time helping to stimulate the brain and provide a distraction.
            </p>
          </div>

          {/* Related Resources Section */}
          <div className="mt-12 sm:mt-16 pt-10 sm:pt-12 border-t border-slate-700/40">
            <h2 className="text-2xl sm:text-3xl font-semibold mb-3 bg-gradient-to-r from-emerald-200 to-teal-200 bg-clip-text text-transparent">
              Recommended Products for Autism Support
            </h2>
            <p className="text-sm sm:text-base text-slate-300/80 mb-6 sm:mb-8 max-w-2xl">
              These sensory tools help support individuals with autism through calming tactile engagement and focus support.
            </p>

            <div className="grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              <Link
                to="/samboards"
                className="group rounded-2xl bg-slate-900/75 border border-slate-700/80 hover:border-emerald-300/60 shadow-[0_16px_45px_rgba(15,23,42,0.8)] p-4 sm:p-5 flex flex-col interactive-card touch-manipulation"
              >
                <h3 className="font-heading text-lg sm:text-xl mb-2 text-emerald-100">Samboards</h3>
                <p className="text-xs sm:text-sm text-slate-200/90 mb-3 sm:mb-4">Full-size boards designed to help manage frustration and support focus.</p>
                <span className="mt-auto inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-emerald-300">
                  View product
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </span>
              </Link>

              <Link
                to="/sensory-pads"
                className="group rounded-2xl bg-slate-900/75 border border-slate-700/80 hover:border-emerald-300/60 shadow-[0_16px_45px_rgba(15,23,42,0.8)] p-4 sm:p-5 flex flex-col interactive-card touch-manipulation"
              >
                <h3 className="font-heading text-lg sm:text-xl mb-2 text-emerald-100">Sensory Pads</h3>
                <p className="text-xs sm:text-sm text-slate-200/90 mb-3 sm:mb-4">Calming tactile pads to help stimulate the brain and provide distraction.</p>
                <span className="mt-auto inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-emerald-300">
                  View product
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </span>
              </Link>

              <Link
                to="/library-squares"
                className="group rounded-2xl bg-slate-900/75 border border-slate-700/80 hover:border-emerald-300/60 shadow-[0_16px_45px_rgba(15,23,42,0.8)] p-4 sm:p-5 flex flex-col interactive-card touch-manipulation"
              >
                <h3 className="font-heading text-lg sm:text-xl mb-2 text-emerald-100">Library Squares</h3>
                <p className="text-xs sm:text-sm text-slate-200/90 mb-3 sm:mb-4">Quiet tactile tiles perfect for classrooms and learning environments.</p>
                <span className="mt-auto inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-emerald-300">
                  View product
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
      </Page>
    </div>
    </>
  );
}
